import { useEffect, useState } from 'react';
import adminApi from '@/lib/adminApi';

const AdminSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testStatus, setTestStatus] = useState('');
  const [testError, setTestError] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [savingProtection, setSavingProtection] = useState(false);
  const [protectionStatus, setProtectionStatus] = useState('');
  const [protectionError, setProtectionError] = useState('');
  const [inviteMessages, setInviteMessages] = useState({ artist_message: '', collaborator_message: '' });
  const [savingInvites, setSavingInvites] = useState(false);
  const [inviteStatus, setInviteStatus] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [artists, setArtists] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [pushForm, setPushForm] = useState({
    audience: 'all_artists',
    recipient_ids: [],
    title: '',
    message: '',
  });
  const [pushReadyOnly, setPushReadyOnly] = useState(true);
  const [sendingPush, setSendingPush] = useState(false);
  const [pushStatus, setPushStatus] = useState('');
  const [pushError, setPushError] = useState('');

  const artistPushCount = artists.filter((artist) => artist.push_notifications_opted_in || Number(artist.push_notification_device_count || 0) > 0).length;
  const collaboratorPushCount = collaborators.filter((collaborator) => collaborator.push_notifications_opted_in || Number(collaborator.push_notification_device_count || 0) > 0).length;
  const artistDeviceCount = artists.reduce((total, artist) => total + Number(artist.push_notification_device_count || 0), 0);
  const collaboratorDeviceCount = collaborators.reduce((total, collaborator) => total + Number(collaborator.push_notification_device_count || 0), 0);
  const pushReadyArtists = artists.filter((artist) => artist.push_notifications_opted_in || Number(artist.push_notification_device_count || 0) > 0);
  const pushReadyCollaborators = collaborators.filter((collaborator) => collaborator.push_notifications_opted_in || Number(collaborator.push_notification_device_count || 0) > 0);
  const artistPushOptions = pushReadyOnly ? pushReadyArtists : artists;
  const collaboratorPushOptions = pushReadyOnly ? pushReadyCollaborators : collaborators;

  useEffect(() => {
    let mounted = true;

    const loadSettings = async () => {
      try {
        const [settingsResponse, artistsResponse, collaboratorsResponse] = await Promise.all([
          adminApi.get('/admin/settings'),
          adminApi.get('/admin/artists'),
          adminApi.get('/admin/collaborators'),
        ]);
        if (mounted) {
          setSettings(settingsResponse.data.settings);
          setInviteMessages(settingsResponse.data.settings.invite_messages || { artist_message: '', collaborator_message: '' });
          setArtists(artistsResponse.data.artists || []);
          setCollaborators(collaboratorsResponse.data.collaborators || []);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadSettings();

    return () => {
      mounted = false;
    };
  }, []);

  const sendTestEmail = async () => {
    setSendingTest(true);
    setTestStatus('');
    setTestError('');

    try {
      const response = await adminApi.post('/admin/email-test');
      setTestStatus(response.data.message || 'Test email sent.');
    } catch (error) {
      setTestError(error.response?.data?.detail || 'Unable to send test email.');
    } finally {
      setSendingTest(false);
    }
  };

  const updateContentProtection = async (nextSettings) => {
    setSavingProtection(true);
    setProtectionStatus('');
    setProtectionError('');

    try {
      const response = await adminApi.put('/admin/settings/content-protection', nextSettings);
      setSettings((current) => ({
        ...current,
        content_protection: response.data.settings,
      }));
      setProtectionStatus('Content protection updated.');
    } catch (error) {
      setProtectionError(error.response?.data?.detail || 'Unable to update content protection.');
    } finally {
      setSavingProtection(false);
    }
  };

  const saveInviteMessages = async () => {
    setSavingInvites(true);
    setInviteStatus('');
    setInviteError('');

    try {
      const response = await adminApi.put('/admin/settings/invite-messages', inviteMessages);
      setSettings((current) => ({
        ...current,
        invite_messages: response.data.settings,
      }));
      setInviteMessages(response.data.settings);
      setInviteStatus('Invite messages saved.');
    } catch (error) {
      setInviteError(error.response?.data?.detail || 'Unable to save invite messages.');
    } finally {
      setSavingInvites(false);
    }
  };

  const copyInviteMessage = async (message, label) => {
    try {
      await navigator.clipboard.writeText(message);
      setInviteStatus(`${label} invite copied.`);
      setInviteError('');
    } catch (error) {
      setInviteError('Unable to copy invite message in this browser.');
    }
  };

  const sendPushNotification = async () => {
    setSendingPush(true);
    setPushStatus('');
    setPushError('');

    try {
      const response = await adminApi.post('/admin/push-notifications', pushForm);
      setPushStatus(response.data.message || 'Push notification sent.');
      setPushForm((current) => ({ ...current, title: '', message: '' }));
    } catch (error) {
      setPushError(error.response?.data?.detail || 'Unable to send push notification.');
    } finally {
      setSendingPush(false);
    }
  };

  const emailSettings = settings
    ? {
        email_notifications_enabled: settings.email_notifications_enabled,
        email_provider: settings.email_provider,
        resend_configured: settings.resend_configured,
        email_from: settings.email_from,
        admin_alert_email: settings.admin_alert_email,
        site_url: settings.site_url,
      }
    : {};

  const operationalSettings = settings
    ? Object.fromEntries(
        Object.entries(settings).filter(
          ([key]) =>
            ![
              'email_notifications_enabled',
              'email_provider',
              'resend_configured',
              'email_from',
              'admin_alert_email',
              'site_url',
              'content_protection',
              'invite_messages',
            ].includes(key)
        )
      )
    : {};
  const contentProtection = settings?.content_protection || {};

  const renderValue = (value) => {
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    return String(value);
  };

  return (
    <div className="space-y-6">
      <section className="bg-white rounded-[2rem] shadow-lg p-6 sm:p-8 border border-orange-100">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-teal-700">Content protection</p>
            <h3 className="text-2xl font-bold text-gray-900 mt-2">Right-click and copy controls</h3>
            <p className="text-gray-600 mt-2 max-w-3xl">
              This deters right-click, copy, cut, and drag on public website pages. Browsers cannot fully block screenshots or screen recording.
            </p>
          </div>
          <button
            type="button"
            onClick={() => updateContentProtection({ ...contentProtection, enabled: !contentProtection.enabled })}
            disabled={savingProtection}
            className={`rounded-full px-6 py-3 font-semibold shadow-lg transition disabled:cursor-not-allowed disabled:bg-gray-300 ${
              contentProtection.enabled ? 'bg-teal-700 text-white hover:bg-teal-800' : 'bg-gray-900 text-white hover:bg-black'
            }`}
          >
            {savingProtection ? 'Saving...' : contentProtection.enabled ? 'Protection On' : 'Protection Off'}
          </button>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          {[
            ['disable_right_click', 'Block right click'],
            ['disable_copy', 'Block copy/cut'],
            ['disable_drag', 'Block image drag'],
          ].map(([key, label]) => (
            <label key={key} className="flex items-center gap-3 rounded-2xl border border-teal-100 bg-teal-50/60 px-4 py-3 text-sm font-semibold text-gray-800">
              <input
                type="checkbox"
                checked={Boolean(contentProtection[key])}
                disabled={savingProtection}
                onChange={(event) => updateContentProtection({ ...contentProtection, [key]: event.target.checked })}
              />
              {label}
            </label>
          ))}
        </div>

        {protectionStatus && <p className="mt-4 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{protectionStatus}</p>}
        {protectionError && <p className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{protectionError}</p>}
      </section>

      <section className="bg-white rounded-[2rem] shadow-lg p-6 sm:p-8 border border-orange-100">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-purple-700">Push notifications</p>
            <h3 className="text-2xl font-bold text-gray-900 mt-2">Send app notifications from admin</h3>
            <p className="text-gray-600 mt-2 max-w-3xl">
              Send a push alert to one artist, one collaborator, all artists, or all collaborators. This goes to Android app users who have notifications enabled.
            </p>
            {!loading && !settings?.push_notifications_enabled && (
              <p className="mt-3 text-sm text-gray-500">
                Android push is not configured in Render yet. Add the Firebase service account credentials in Render and redeploy the backend to enable sending.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={sendPushNotification}
            disabled={sendingPush || !settings?.push_notifications_enabled}
            className="rounded-full bg-purple-700 px-6 py-3 font-semibold text-white shadow-lg transition hover:bg-purple-800 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {sendingPush ? 'Sending...' : 'Send push notification'}
          </button>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-3xl border border-sky-100 bg-sky-50 px-5 py-4">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-sky-700">Artists opted-in</p>
            <p className="mt-2 text-3xl font-bold text-gray-900">{artistPushCount}</p>
            <p className="mt-1 text-xs text-gray-600">{artistDeviceCount} active app device(s)</p>
          </div>
          <div className="rounded-3xl border border-sky-100 bg-sky-50 px-5 py-4">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-sky-700">Collaborators opted-in</p>
            <p className="mt-2 text-3xl font-bold text-gray-900">{collaboratorPushCount}</p>
            <p className="mt-1 text-xs text-gray-600">{collaboratorDeviceCount} active app device(s)</p>
          </div>
          <div className="rounded-3xl border border-gray-100 bg-gray-50 px-5 py-4 lg:col-span-2">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-gray-600">Admin note</p>
            <p className="mt-2 text-sm text-gray-700">
              Individual dropdowns show Push ON/OFF. Push can only reach users with at least one active app device.
            </p>
          </div>
        </div>

        <label className="mt-5 inline-flex items-center gap-3 rounded-full border border-sky-100 bg-white px-4 py-2 text-sm font-bold text-gray-700">
          <input
            type="checkbox"
            checked={pushReadyOnly}
            onChange={(event) => {
              setPushReadyOnly(event.target.checked);
              setPushForm((current) => ({ ...current, recipient_ids: [] }));
            }}
          />
          Show only push-ready users in individual recipient lists
        </label>

        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <div>
            <label className="text-sm font-bold text-gray-900">Audience</label>
            <select
              value={pushForm.audience}
              onChange={(event) =>
                setPushForm({
                  audience: event.target.value,
                  recipient_ids: [],
                  title: pushForm.title,
                  message: pushForm.message,
                })
              }
              className="mt-2 w-full rounded-2xl border border-purple-100 px-4 py-3 text-sm text-gray-800"
            >
              <option value="all_artists">All artists</option>
              <option value="all_collaborators">All collaborators</option>
              <option value="artist">One artist</option>
              <option value="collaborator">One collaborator</option>
            </select>
          </div>

          {pushForm.audience === 'artist' && (
            <div>
              <label className="text-sm font-bold text-gray-900">Select artist</label>
              <select
                value={pushForm.recipient_ids[0] || ''}
                onChange={(event) => setPushForm((current) => ({ ...current, recipient_ids: event.target.value ? [event.target.value] : [] }))}
                className="mt-2 w-full rounded-2xl border border-purple-100 px-4 py-3 text-sm text-gray-800"
              >
                <option value="">Choose artist</option>
                {artistPushOptions.map((artist) => (
                  <option key={artist.id} value={artist.id}>
                    {artist.name} {artist.city ? `| ${artist.city}` : ''} {artist.push_notifications_opted_in ? `| Push ON (${artist.push_notification_device_count || 1})` : '| Push OFF'}
                  </option>
                ))}
              </select>
            </div>
          )}

          {pushForm.audience === 'collaborator' && (
            <div>
              <label className="text-sm font-bold text-gray-900">Select collaborator</label>
              <select
                value={pushForm.recipient_ids[0] || ''}
                onChange={(event) => setPushForm((current) => ({ ...current, recipient_ids: event.target.value ? [event.target.value] : [] }))}
                className="mt-2 w-full rounded-2xl border border-purple-100 px-4 py-3 text-sm text-gray-800"
              >
                <option value="">Choose collaborator</option>
                {collaboratorPushOptions.map((collaborator) => (
                  <option key={collaborator.id} value={collaborator.id}>
                    {(collaborator.organization_name || collaborator.name)} {collaborator.city ? `| ${collaborator.city}` : ''} {collaborator.push_notifications_opted_in ? `| Push ON (${collaborator.push_notification_device_count || 1})` : '| Push OFF'}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="lg:col-span-2">
            <label className="text-sm font-bold text-gray-900">Notification title</label>
            <input
              value={pushForm.title}
              onChange={(event) => setPushForm((current) => ({ ...current, title: event.target.value }))}
              placeholder="For example: KalaManch update"
              className="mt-2 w-full rounded-2xl border border-purple-100 px-4 py-3 text-sm text-gray-800"
            />
          </div>

          <div className="lg:col-span-2">
            <label className="text-sm font-bold text-gray-900">Notification message</label>
            <textarea
              value={pushForm.message}
              onChange={(event) => setPushForm((current) => ({ ...current, message: event.target.value }))}
              rows="4"
              placeholder="Write the push message users should see on their phone."
              className="mt-2 w-full rounded-2xl border border-purple-100 px-4 py-3 text-sm text-gray-800"
            />
          </div>
        </div>

        {pushStatus && <p className="mt-4 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{pushStatus}</p>}
        {pushError && <p className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{pushError}</p>}
      </section>

      <section className="bg-white rounded-[2rem] shadow-lg p-6 sm:p-8 border border-orange-100">
        <h2 className="text-3xl font-bold text-gray-900">Settings</h2>
        <p className="text-gray-600 mt-3">
          These are the current live operational settings. They stay read-only for now so admin can monitor the platform safely without changing production behavior accidentally.
        </p>
      </section>

      <section className="bg-white rounded-[2rem] shadow-lg p-6 sm:p-8 border border-orange-100">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-orange-600">Invite copy</p>
            <h3 className="text-2xl font-bold text-gray-900 mt-2">Manage copy invite messages</h3>
            <p className="text-gray-600 mt-2 max-w-3xl">
              Edit the messages admin can copy and send on WhatsApp, email, Instagram, or Facebook when inviting artists and collaborators.
            </p>
          </div>
          <button
            type="button"
            onClick={saveInviteMessages}
            disabled={savingInvites}
            className="rounded-full bg-orange-600 px-6 py-3 font-semibold text-white shadow-lg transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {savingInvites ? 'Saving...' : 'Save invite messages'}
          </button>
        </div>

        <div className="mt-6 grid gap-5 lg:grid-cols-2">
          <div>
            <label className="text-sm font-bold text-gray-900">Artist invite message</label>
            <textarea
              value={inviteMessages.artist_message || ''}
              onChange={(event) => setInviteMessages((current) => ({ ...current, artist_message: event.target.value }))}
              rows="6"
              className="mt-2 w-full rounded-2xl border border-orange-100 px-4 py-3 text-sm text-gray-800"
            />
            <button
              type="button"
              onClick={() => copyInviteMessage(inviteMessages.artist_message || '', 'Artist')}
              className="mt-3 rounded-full border border-orange-200 px-4 py-2 text-sm font-semibold text-orange-700 hover:bg-orange-50"
            >
              Copy artist invite
            </button>
          </div>
          <div>
            <label className="text-sm font-bold text-gray-900">Collaborator invite message</label>
            <textarea
              value={inviteMessages.collaborator_message || ''}
              onChange={(event) => setInviteMessages((current) => ({ ...current, collaborator_message: event.target.value }))}
              rows="6"
              className="mt-2 w-full rounded-2xl border border-teal-100 px-4 py-3 text-sm text-gray-800"
            />
            <button
              type="button"
              onClick={() => copyInviteMessage(inviteMessages.collaborator_message || '', 'Collaborator')}
              className="mt-3 rounded-full border border-teal-200 px-4 py-2 text-sm font-semibold text-teal-700 hover:bg-teal-50"
            >
              Copy collaborator invite
            </button>
          </div>
        </div>

        {inviteStatus && <p className="mt-4 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{inviteStatus}</p>}
        {inviteError && <p className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{inviteError}</p>}
      </section>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <div className="sm:col-span-2 xl:col-span-3">
          <h3 className="text-2xl font-bold text-gray-900">System status</h3>
          <p className="mt-2 text-sm text-gray-600">
            These cards explain backend runtime values for admin diagnostics. They are not public website content.
          </p>
        </div>
        {loading && <div className="text-gray-500">Loading settings...</div>}
        {!loading &&
          Object.entries(operationalSettings).map(([key, value]) => (
            <div key={key} className="bg-white rounded-[1.75rem] shadow-lg p-5 border border-orange-100">
              <p className="text-sm text-gray-500">{key.replaceAll('_', ' ')}</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{renderValue(value)}</p>
            </div>
          ))}
      </section>

      <section className="bg-white rounded-[2rem] shadow-lg p-6 sm:p-8 border border-orange-100">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-orange-600">Email diagnostics</p>
            <h3 className="text-2xl font-bold text-gray-900 mt-2">Admin notification test</h3>
            <p className="text-gray-600 mt-2 max-w-3xl">
              Use this before launch to confirm contact forms, signups, enquiries, and PMB alerts can send emails to the admin inbox.
            </p>
          </div>
          <button
            type="button"
            onClick={sendTestEmail}
            disabled={sendingTest || !emailSettings.resend_configured || !emailSettings.email_notifications_enabled}
            className="rounded-full bg-orange-600 px-6 py-3 font-semibold text-white shadow-lg transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {sendingTest ? 'Sending...' : 'Send test email'}
          </button>
        </div>

        {!loading && (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 mt-6">
            {Object.entries(emailSettings).map(([key, value]) => (
              <div key={key} className="rounded-2xl border border-orange-100 bg-orange-50/50 p-4">
                <p className="text-sm text-gray-500">{key.replaceAll('_', ' ')}</p>
                <p className="mt-1 break-words text-lg font-bold text-gray-900">{renderValue(value)}</p>
              </div>
            ))}
          </div>
        )}

        {!loading && !emailSettings.resend_configured && (
          <p className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            RESEND_API_KEY is not configured in Render yet, so test emails and launch notifications cannot send.
          </p>
        )}
        {testStatus && <p className="mt-4 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{testStatus}</p>}
        {testError && <p className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{testError}</p>}
      </section>
    </div>
  );
};

export default AdminSettings;
